// Task #3041 — Modale galleria avatar del PA ("il tuo dipendente").
//
// Interruttore "Abilita avatar" (default OFF = icona attuale) + galleria di
// scelta: stile DiceBear, 12 varianti deterministiche, personalizzazione base
// (incarnato/capelli/sfondo/flip secondo lo stile) con anteprima live, e nome
// dell'assistente. Le scelte sono personali (per-utente) e restano persistite
// anche a interruttore spento.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Shuffle } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@easyflow/ui-frontend/components/ui/dialog';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { Switch } from '@easyflow/ui-frontend/components/ui/switch';
import { Input } from '@easyflow/ui-frontend/components/ui/input';
import { Label } from '@easyflow/ui-frontend/components/ui/label';
import { ScrollArea } from '@easyflow/ui-frontend/components/ui/scroll-area';
import { useToast } from '@/hooks/use-toast';
import type { PaAvatarConfig, PaAvatarPrefs, PaAvatarPrefsUpdate, PaAvatarStyle } from '@easyflow/shared';
import { PaFace } from './PaFace';
import {
  PA_AVATAR_BACKGROUNDS,
  PA_AVATAR_HAIR_COLORS,
  PA_AVATAR_SKIN_TONES,
  PA_AVATAR_STYLE_DEFS,
  defaultPaAvatarConfig,
  getPaAvatarStyleDef,
  paAvatarDataUri,
} from './paAvatarStyles';

const VARIANT_COUNT = 12;

interface AvatarSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  prefs: PaAvatarPrefs;
  /** Seed deterministico personale (fallback quando non c'è ancora una scelta). */
  defaultSeed: string;
  /** Colore tema + icona attuale, per l'anteprima dello stato OFF. */
  assistantColor: string;
  FallbackIcon: React.ComponentType<{ className?: string }>;
  /** Nome dell'agente PA (placeholder quando non c'è un nome personalizzato). */
  baseAssistantName: string;
  onSave: (update: PaAvatarPrefsUpdate) => Promise<PaAvatarPrefs>;
}

/** Swatch colore cliccabile della palette (hex senza `#`; null = predefinito). */
const ColorSwatch: React.FC<{
  hex: string | null;
  selected: boolean;
  onSelect: () => void;
  title: string;
}> = ({ hex, selected, onSelect, title }) => (
  <button
    type="button"
    onClick={onSelect}
    title={title}
    className={`h-6 w-6 rounded-full border transition-shadow ${
      selected ? 'ring-2 ring-violet-500 ring-offset-1 border-transparent' : 'border-border'
    } ${hex ? '' : 'bg-muted text-[9px] font-medium text-muted-foreground'}`}
    style={hex ? { backgroundColor: `#${hex}` } : undefined}
  >
    {hex ? null : '—'}
  </button>
);

export const AvatarSettingsDialog: React.FC<AvatarSettingsDialogProps> = ({
  open,
  onOpenChange,
  prefs,
  defaultSeed,
  assistantColor,
  FallbackIcon,
  baseAssistantName,
  onSave,
}) => {
  const { toast } = useToast();
  const [enabled, setEnabled] = useState(false);
  const [name, setName] = useState('');
  const [draft, setDraft] = useState<PaAvatarConfig>(() => defaultPaAvatarConfig(defaultSeed));
  const [seedNonce, setSeedNonce] = useState(0);
  const [saving, setSaving] = useState(false);

  // Ri-inizializza la bozza dalle preferenze persistite a ogni apertura.
  useEffect(() => {
    if (!open) return;
    setEnabled(prefs.enabled);
    setName(prefs.assistantName ?? '');
    setDraft(prefs.avatar ?? defaultPaAvatarConfig(defaultSeed));
    setSeedNonce(0);
  }, [open, prefs, defaultSeed]);

  const styleDef = getPaAvatarStyleDef(draft.style);
  const options = draft.options ?? {};

  // 12 varianti deterministiche dal seed personale; "Mescola" aggiunge un
  // nonce così l'utente esplora facce nuove restando deterministico.
  const variantSeeds = useMemo(() => {
    const base = seedNonce === 0 ? defaultSeed : `${defaultSeed}-r${seedNonce}`;
    return Array.from({ length: VARIANT_COUNT }, (_, i) => (i === 0 ? base : `${base}-v${i}`));
  }, [defaultSeed, seedNonce]);

  const setOption = useCallback((key: 'backgroundColor' | 'skinColor' | 'hairColor', value: string | null) => {
    setDraft(prev => {
      const next = { ...(prev.options ?? {}) };
      if (value === null) delete next[key];
      else next[key] = value;
      return { ...prev, options: next };
    });
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      await onSave({
        enabled,
        assistantName: name.trim() ? name.trim() : null,
        avatar: draft,
      });
      toast({ title: 'Avatar salvato', description: enabled ? 'Il tuo assistente ha un nuovo volto.' : 'Avatar disattivato: resta l\'icona classica (la scelta è conservata).' });
      onOpenChange(false);
    } catch (err) {
      toast({
        title: 'Salvataggio non riuscito',
        description: err instanceof Error ? err.message : 'Riprova tra qualche istante.',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  }, [enabled, name, draft, onSave, toast, onOpenChange]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* DialogContent è `sm:grid` di default: qui serve colonna flex con
          altezza definita perché la galleria scorra dentro la ScrollArea. */}
      <DialogContent className="sm:max-w-xl sm:flex sm:flex-col sm:h-[min(80vh,680px)] p-0 gap-0">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border shrink-0">
          <DialogTitle>Avatar del tuo assistente</DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between px-5 py-3 border-b border-border shrink-0">
          <div className="min-w-0 pr-3">
            <Label htmlFor="pa-avatar-enabled" className="text-sm font-medium">Abilita avatar</Label>
            <p className="text-xs text-muted-foreground">
              Spento: resta l'icona classica. Acceso: scegli volto e nome del tuo assistente.
            </p>
          </div>
          <Switch id="pa-avatar-enabled" checked={enabled} onCheckedChange={setEnabled} />
        </div>

        {enabled ? (
          <ScrollArea className="flex-1 min-h-0">
            <div className="px-5 py-4 space-y-5">
              {/* Anteprima live + nome */}
              <div className="flex items-center gap-4">
                <PaFace
                  avatarEnabled
                  avatarConfig={draft}
                  defaultSeed={defaultSeed}
                  color={assistantColor}
                  FallbackIcon={FallbackIcon}
                  size={72}
                  showStateLayer={false}
                />
                <div className="flex-1 min-w-0 space-y-1">
                  <Label htmlFor="pa-avatar-name" className="text-xs text-muted-foreground">
                    Nome del tuo assistente
                  </Label>
                  <Input
                    id="pa-avatar-name"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    maxLength={60}
                    placeholder={baseAssistantName}
                  />
                </div>
              </div>

              {/* Stili */}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Stile</Label>
                <div className="grid grid-cols-3 gap-2">
                  {PA_AVATAR_STYLE_DEFS.map(def => {
                    const selected = def.id === draft.style;
                    return (
                      <button
                        key={def.id}
                        type="button"
                        onClick={() => setDraft(prev => ({ ...prev, style: def.id as PaAvatarStyle }))}
                        className={`flex flex-col items-center gap-1.5 rounded-lg border p-2 text-xs transition-colors ${
                          selected ? 'border-violet-500 bg-violet-50 dark:bg-violet-950/30' : 'border-border hover:bg-muted'
                        }`}
                      >
                        <img
                          src={paAvatarDataUri({ style: def.id, seed: draft.seed, options })}
                          alt=""
                          className="h-12 w-12 rounded-full bg-white/90"
                          draggable={false}
                        />
                        <span className="font-medium text-foreground">{def.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Varianti */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label className="text-xs text-muted-foreground">Variante</Label>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 gap-1.5 px-2 text-xs"
                    onClick={() => setSeedNonce(n => n + 1)}
                  >
                    <Shuffle className="h-3.5 w-3.5" />
                    Mescola
                  </Button>
                </div>
                <div className="grid grid-cols-6 gap-2">
                  {variantSeeds.map(seed => {
                    const selected = seed === draft.seed;
                    return (
                      <button
                        key={seed}
                        type="button"
                        onClick={() => setDraft(prev => ({ ...prev, seed }))}
                        className={`rounded-full border p-0.5 transition-shadow ${
                          selected ? 'ring-2 ring-violet-500 border-transparent' : 'border-border hover:border-violet-300'
                        }`}
                        title="Scegli questa variante"
                      >
                        <img
                          src={paAvatarDataUri({ style: draft.style, seed, options })}
                          alt=""
                          className="h-10 w-10 rounded-full bg-white/90"
                          draggable={false}
                        />
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Personalizzazione base (secondo lo stile) */}
              {styleDef.supportsSkin && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Incarnato</Label>
                  <div className="flex flex-wrap gap-2">
                    <ColorSwatch hex={null} selected={!options.skinColor} onSelect={() => setOption('skinColor', null)} title="Predefinito" />
                    {PA_AVATAR_SKIN_TONES.map(hex => (
                      <ColorSwatch key={hex} hex={hex} selected={options.skinColor === hex} onSelect={() => setOption('skinColor', hex)} title={`#${hex}`} />
                    ))}
                  </div>
                </div>
              )}
              {styleDef.supportsHair && (
                <div className="space-y-2">
                  <Label className="text-xs text-muted-foreground">Capelli</Label>
                  <div className="flex flex-wrap gap-2">
                    <ColorSwatch hex={null} selected={!options.hairColor} onSelect={() => setOption('hairColor', null)} title="Predefinito" />
                    {PA_AVATAR_HAIR_COLORS.map(hex => (
                      <ColorSwatch key={hex} hex={hex} selected={options.hairColor === hex} onSelect={() => setOption('hairColor', hex)} title={`#${hex}`} />
                    ))}
                  </div>
                </div>
              )}
              <div className="space-y-2">
                <Label className="text-xs text-muted-foreground">Sfondo</Label>
                <div className="flex flex-wrap gap-2">
                  <ColorSwatch hex={null} selected={!options.backgroundColor} onSelect={() => setOption('backgroundColor', null)} title="Nessuno" />
                  {PA_AVATAR_BACKGROUNDS.map(hex => (
                    <ColorSwatch key={hex} hex={hex} selected={options.backgroundColor === hex} onSelect={() => setOption('backgroundColor', hex)} title={`#${hex}`} />
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between">
                <Label htmlFor="pa-avatar-flip" className="text-xs text-muted-foreground">Specchia orizzontalmente</Label>
                <Switch
                  id="pa-avatar-flip"
                  checked={options.flip === true}
                  onCheckedChange={checked =>
                    setDraft(prev => {
                      const next = { ...(prev.options ?? {}) };
                      if (checked) next.flip = true;
                      else delete next.flip;
                      return { ...prev, options: next };
                    })
                  }
                />
              </div>
            </div>
          </ScrollArea>
        ) : (
          <div className="flex-1 min-h-0 flex flex-col items-center justify-center gap-3 px-5 py-8 text-center">
            <div
              className="w-14 h-14 rounded-full flex items-center justify-center text-white"
              style={{ backgroundColor: assistantColor }}
            >
              <FallbackIcon className="h-7 w-7" />
            </div>
            <p className="text-sm text-muted-foreground max-w-sm">
              L'assistente usa l'icona classica. Attiva l'avatar per dargli un volto e un nome:
              la scelta già fatta resta salvata e la ritrovi riattivandolo.
            </p>
          </div>
        )}

        <DialogFooter className="sm:px-5 sm:py-3 sm:border-t sm:border-border shrink-0">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            Annulla
          </Button>
          <Button onClick={() => { void handleSave(); }} disabled={saving}>
            {saving ? 'Salvataggio…' : 'Salva'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
